import { z } from "zod";
import { isOrderColor } from "./order-domain";

const text = (length: number) => z.string().trim().min(1).max(length);
const nullableText = (length: number) => z.preprocess(
  (value) => (typeof value === "string" && !value.trim()) || value === undefined ? null : value,
  text(length).nullable(),
);

const money = z.union([z.string(), z.number()])
  .transform((value) => String(value).trim())
  .pipe(z.string().regex(/^\d{1,7}(?:\.\d{1,2})?$/, "金额须为非负数，最多两位小数"))
  .transform((value) => Math.round(Number(value) * 100));
const nullableMoney = z.preprocess(
  (value) => value === "" || value === null || value === undefined ? null : value,
  money.nullable(),
);

export const orderIdSchema = z.coerce.number().int().positive();
export const orderItemWriteSchema = z.object({
  id: orderIdSchema.optional(), file_name: text(255), file_key: text(512),
  color: text(100), quantity: z.coerce.number().int().min(1).max(999),
}).strict().refine((item) => item.id !== undefined || isOrderColor(item.color), { path: ["color"], message: "请选择黑色哑光PETG或白色PETG" });

export const orderWriteSchema = z.object({
  order_time: z.iso.datetime({ local: true, error: "下单时间无效" }).max(40),
  xianyu_id: nullableText(100), address: text(1000), total_price: nullableMoney, notes: nullableText(1000),
  items: z.array(orderItemWriteSchema).min(1).max(100),
}).strict().superRefine((input, context) => {
  if (new Set(input.items.map((item) => item.file_key)).size !== input.items.length) {
    context.addIssue({ code: "custom", path: ["items"], message: "同一文件不能重复添加，请调整数量" });
  }
  const ids = input.items.flatMap((item) => item.id === undefined ? [] : [item.id]);
  if (new Set(ids).size !== ids.length) {
    context.addIssue({ code: "custom", path: ["items"], message: "文件条目重复" });
  }
}).transform(({ total_price, ...input }) => ({ ...input, total_price_cents: total_price }));

export const shipmentSchema = z.object({ tracking_number: text(100).optional(), quote: money.optional() }).strict()
  .refine((input) => input.tracking_number !== undefined || input.quote !== undefined, "请填写单号或报价")
  .transform(({ quote, tracking_number }) => ({
    ...(tracking_number === undefined ? {} : { tracking_number }),
    ...(quote === undefined ? {} : { quote_cents: quote }),
  }));
export const receiptSchema = z.object({ quote_cents: z.number().int().min(0).max(999999999) }).strict();
export const addressCopySchema = z.object({ address: text(1000) }).strict();
export const cancelReceiptSchema = z.object({ paid_at: z.iso.datetime() }).strict();
export type OrderWrite = z.output<typeof orderWriteSchema>;
export type ShipmentWrite = z.output<typeof shipmentSchema>;
